<template>
    <validation-observer ref="form" v-slot="{ meta, setFieldValue, errors }">
        <modal
            @hidden="clearData"
            @shown="setData"
            :id="modalId"
            :ok-disabled="!meta.valid || busy"
            :title="title"
            @ok.prevent="submit"
        >
            <validation-provider name="title" rules="required">
                <form-input
                    :error="errors.title"
                    v-model="values.title"
                    label="Name"
                    @input="(val) => setFieldValue('title', val)"
                />
            </validation-provider>
            <form-textarea v-model="values.description" label="Description" />
            <div class="d-flex justify-content-between input-wrapper align-items-baseline flex-md-row flex-column">
                <validation-provider name="value" class="w-100" rules="required|validNumber" as="div">
                    <form-input
                        class="mb-md-3 mb-0"
                        :error="errors.value"
                        v-model="values.value"
                        label="Value"
                        @input="(val) => setFieldValue('value', val)"
                    />
                </validation-provider>
                <validation-provider name="value_type" class="w-100" rules="required" as="div">
                    <form-select
                        class="mb-md-3 mb-0"
                        :disabled="isCleaningFee"
                        :options="valueOptions"
                        :error="errors[0]"
                        v-model="values.value_type"
                        @change="(val) => setFieldValue('value_type', val)"
                    />
                </validation-provider>
                <validation-provider
                    v-if="values.value_type === 0"
                    class="w-100"
                    rules="required"
                    name="frequency"
                    as="div"
                >
                    <form-select
                        @change="onChangeFrequency"
                        :disabled="isCleaningFee"
                        :options="frequencyOptions"
                        :error="errors.frequency"
                        v-model="values.frequency"
                    />
                </validation-provider>
            </div>
            <div class="d-flex justify-content-between input-wrapper align-items-baseline flex-md-row flex-column">
                <validation-provider name="type" class="w-100" rules="required" as="div">
                    <form-select
                        class="mb-md-3 mb-0"
                        :options="feeTypeOptions"
                        @change="onChangeType"
                        :error="errors[0]"
                        v-model="values.type"
                        label="Type"
                    />
                </validation-provider>
                <validation-provider name="group" class="w-100" rules="required" as="div">
                    <form-select
                        @change="onChangeGroup"
                        :options="feeGroupOptions"
                        :error="errors.group"
                        v-model="values.group"
                        label="Group"
                    />
                </validation-provider>
            </div>
            <validation-provider name="override_universal_rules" class="w-100" as="div">
                <form-select
                    :options="collectionRulesOptions"
                    label="Collection Rules"
                    :error="errors.override_universal_rules"
                    v-model="values.override_universal_rules"
                    @change="(val) => setFieldValue('override_universal_rules', val)"
                />
            </validation-provider>
            <validation-provider
                v-if="metadata && metadata.has_property_management"
                class="w-100"
                rules="required"
                name="commissionable"
                as="div"
            >
                <form-select
                    :options="ownerStatementOptions"
                    label="Owner Statement"
                    :error="errors.commissionable"
                    v-model="values.commissionable"
                    @change="(val) => setFieldValue('commissionable', val)"
                />
            </validation-provider>
            <div
                class="d-flex justify-content-between input-wrapper align-items-baseline flex-md-row flex-column mb-md-3 mb-0"
                v-if="values.value_type == 0 && values.frequency == 'ONETIME'"
            >
                <form-select
                    :class="{ 'mb-0': values.is_advanced }"
                    :options="conditionOptions"
                    :error="errors[0]"
                    v-model="values.is_advanced"
                    label="Conditional?"
                />
                <div class="w-100 d-flex input-wrapper align-items-baseline mb-md-3 mb-0" v-if="values.is_advanced">
                    <validation-provider name="values.value" class="w-100" rules="validPositiveOrZeroFloat" as="div">
                        <form-input
                            :error="errors['values.value']"
                            v-model="values.values.value"
                            @input="(val) => setFieldValue('values.value', val)"
                        />
                    </validation-provider>
                    <span>after</span>
                    <validation-provider name="values.nights" class="w-100" rules="validPositiveOrZeroFloat" as="div">
                        <form-input
                            :error="errors['values.nights']"
                            v-model="values.values.nights"
                            @input="(val) => setFieldValue('values.nights', val)"
                        />
                    </validation-provider>
                    <span>nights</span>
                </div>
            </div>
            <form-search-multiselect
                v-model="values.tax_exceptions"
                label="Taxes"
                :options="taxOptions"
                placeholder="Select Taxes"
            />

            <small> Edits will appear on all properties where this fee currently appears. </small>
        </modal>
    </validation-observer>
</template>

<script>
    import { mapGetters } from 'vuex';
    import formInput from '../../generic/form-input/form-input.vue';
    import formSelect from '../../generic/form-select/form-select.vue';
    import modal from '../../generic/modal/modal.vue';
    import formTextarea from '../../generic/form-textarea/form-textarea.vue';
    import formSearchMultiselect from '../../generic/form-search-multiselect/form-search-multiselect.vue';

    export default {
        name: 'AddEditTaxModal',
        components: {
            formInput,
            formSelect,
            modal,
            formTextarea,
            formSearchMultiselect,
        },
        props: {
            initialData: {
                type: Object,
                default: null,
            },
            modalId: {
                type: String,
            },
            busy: {
                type: Boolean,
            },
        },
        data() {
            return {
                values: {
                    tax_exceptions: [],
                    title: 'New Tax',
                    value: '',
                    is_flat: false,
                    is_advanced: false,
                    id: null,
                    type: 15,
                    frequency: 'ONETIME',
                    values: {
                        value: '0',
                        nights: 1,
                    },
                    group: 1,
                    override_universal_rules: false,
                    commissionable: 0,
                    value_type: 0,
                },
            };
        },
        computed: {
            ...mapGetters('propertyManager', ['metadata']),
            title() {
                return this.initialData ? 'Edit Fee' : 'Add Fee';
            },
            isCleaningFee() {
                return (
                    this.metadata?.fee_group_choices.find((item) => item.id === this.values.group)?.sysname ===
                    'CLEANING_FEE'
                );
            },
            feeTypeOptions() {
                return this.metadata?.fee_type_choices.map((item) => ({
                    value: item.id,
                    text: item.name,
                }));
            },
            feeGroupOptions() {
                return this.metadata?.fee_group_choices.map((item) => ({
                    value: item.id,
                    text: item.name,
                }));
            },
            ownerStatementOptions() {
                return [
                    {
                        value: 0,
                        text: 'Not Applicable',
                    },
                    {
                        value: 1,
                        text: 'Include as revenue (commissioned)',
                    },
                    {
                        value: 2,
                        text: 'Include as revenue (no commission)',
                    },
                ];
            },
            collectionRulesOptions() {
                return [
                    {
                        value: false,
                        text: 'Per Universal Rules',
                    },
                    {
                        value: true,
                        text: 'Fee due at time of booking',
                    },
                ];
            },
            valueOptions() {
                return [
                    {
                        value: 0,
                        text: 'Fixed',
                    },
                    {
                        value: 1,
                        text: 'Percent % Rental Charges',
                    },
                    {
                        value: 2,
                        text: 'Percent % Total Charges',
                    },
                ];
            },
            frequencyOptions() {
                return [
                    {
                        value: 'DAILY',
                        text: 'Daily',
                    },
                    {
                        value: 'ONETIME',
                        text: 'One Time',
                    },
                ];
            },
            conditionOptions() {
                return [
                    {
                        value: false,
                        text: 'No',
                    },
                    {
                        value: true,
                        text: 'Yes',
                    },
                ];
            },
            taxOptions() {
                return this.metadata?.taxes.map((item) => ({
                    value: item.id,
                    text: item.title,
                }));
            },
        },
        methods: {
            async submit() {
                await this.$emit('submit', {
                    ...this.values,
                    type: {
                        ...this.metadata.fee_type_choices.find((item) => item.id === this.values.type),
                    },
                    group: {
                        ...this.metadata.fee_group_choices.find((item) => item.id === this.values.group),
                    },
                    tax_exceptions: this.taxOptions
                        .filter((item) => !this.values.tax_exceptions.includes(item.value))
                        .map((item) => item.value),
                });
            },
            setData() {
                if (this.initialData) {
                    this.values = {
                        ...this.initialData,
                        type: this.initialData.type.id,
                        group: this.initialData.group.id,
                        values: {
                            value: this.initialData.values.length > 0 ? this.initialData.values[0]?.value : '0',
                            nights: this.initialData.values.length > 0 ? this.initialData.values[0]?.nights : 1,
                        },
                        tax_exceptions: this.metadata?.taxes
                            .map((item) => item.id)
                            .filter((item) => !this.initialData.tax_exceptions.map((item) => item.tax).includes(item)),
                    };
                } else {
                    this.values = {
                        title: 'New Fee',
                        value: '',
                        is_flat: false,
                        is_advanced: false,
                        id: null,
                        type: this.feeTypeOptions[0]?.value,
                        frequency: 'ONETIME',
                        values: {
                            value: '0',
                            nights: 1,
                        },
                        group: this.feeGroupOptions[0]?.value,
                        override_universal_rules: false,
                        commissionable: 0,
                        value_type: 0,
                        tax_exceptions: [],
                    };
                }
                this.$refs.form.setValues({
                    ...this.values,
                });
            },
            clearData() {
                this.$emit('hide');
                this.values = {
                    tax_exceptions: [],
                    title: 'New Tax',
                    value: '',
                    is_flat: false,
                    is_advanced: false,
                    id: null,
                    type: 15,
                    frequency: 'ONETIME',
                    values: {
                        value: '0',
                        nights: 1,
                    },
                    group: 1,
                    override_universal_rules: false,
                    commissionable: 0,
                    value_type: 0,
                };
            },
            onChangeGroup(value) {
                const sysname = this.metadata.fee_group_choices.find((item) => item.id === value).sysname;
                if (sysname == 'CLEANING_FEE') {
                    this.values.value_type = 0;
                    this.values.frequency = 'ONETIME';
                }
                if (sysname == 'PET_FEE') {
                    const petFeeType = this.metadata.fee_type_choices.find(
                        (item) => item.sysname === 'PET_FEE_TRIGGER'
                    );
                    this.values.type = petFeeType.id;
                }
                this.$refs.form.setFieldValue('group', value);
            },
            onChangeType(value) {
                const sysname = this.metadata.fee_type_choices.find((item) => item.id === value).sysname;
                if (sysname == 'PET_FEE_TRIGGER') {
                    const petFeeGroup = this.metadata.fee_group_choices.find((item) => item.sysname === 'PET_FEE');
                    this.values.group = petFeeGroup.id;
                }
                this.$refs.form.setFieldValue('type', value);
            },
            onChangeFrequency(value) {
                if (value == 'DAILY') {
                    this.values.is_advanced = false;
                }
                this.$refs.form.setFieldValue('frequency', value);
            },
        },
        watch: {
            initialData() {
                this.setData();
            },
        },
    };
</script>

<style scoped>
    .input-wrapper {
        gap: 1rem;
    }
</style>
